import type { KibanaLogger } from '@revelio/iso-utility';

type ErrorLogger = Pick<KibanaLogger, 'error'>;

const CONTEXT = 'global-error-handler';
// Repeats are dropped first, so this only bites on a page that raises many
// distinct errors, and the first few explain it.
const MAX_ERRORS_PER_PAGE_LOAD = 10;
// Not faults in our code: a chunk deleted by a later deploy, a request that
// failed with nothing handling it (the request layers log their own), and the
// browser deferring resize notifications.
const IGNORED_MESSAGES = [
  'Loading chunk ',
  'Loading CSS chunk ',
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource',
  'ResizeObserver loop',
];
const LOCATION_RE = /[a-z][\w+.-]*:\/\/[^\s()]+:\d+:\d+/i;

const describe = (thrown: unknown, fallback: string): string =>
  thrown instanceof Error ? `${thrown.name}: ${thrown.message}` : fallback;

const isIgnored = (description: string): boolean =>
  IGNORED_MESSAGES.some((message) => description.includes(message));

/**
 * Reports uncaught errors and unhandled rejections raised by the app's own
 * bundle to Kibana, each distinct one once per page load. Returns a function
 * that removes the listeners.
 */
export const installGlobalErrorReporting = (logger: ErrorLogger) => {
  const { origin } = window.location;
  const reported = new Set<string>();

  const report = (
    kind: string,
    description: string,
    location: string | undefined,
    stack: string | undefined
  ) => {
    // Anywhere else is a browser extension, another site or injected code.
    if (!location?.startsWith(`${origin}/static/js/`)) return;
    if (reported.size >= MAX_ERRORS_PER_PAGE_LOAD) return;
    // dash-meta fingerprints the first line for the Slack alert and the
    // digest, so it is short and ends with where the error was raised.
    const summary = description.replace(/\s+/g, ' ').slice(0, 90);
    const headline = `${kind} ${summary} at ${location.slice(origin.length)}`;
    if (reported.has(headline)) return;
    reported.add(headline);
    const message = stack ? `${headline}\n${stack.slice(0, 4000)}` : headline;
    // A failed log request must not become another rejection to report.
    logger.error(message, { context: CONTEXT }).catch(() => undefined);
  };

  const onError = (event: ErrorEvent) => {
    const thrown: unknown = event.error;
    const description = describe(
      thrown,
      event.message.replace(/^Uncaught /, '')
    );
    // A deleted chunk answered with index.html fails to parse at 1:1.
    const staleChunk =
      thrown instanceof SyntaxError && event.lineno === 1 && event.colno <= 1;
    if (staleChunk || isIgnored(description)) return;
    report(
      'Uncaught',
      description,
      `${event.filename}:${event.lineno}:${event.colno}`,
      thrown instanceof Error ? thrown.stack : undefined
    );
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    const { name, message } = (reason ?? {}) as {
      name?: unknown;
      message?: unknown;
    };
    // OpenReplay's patched fetch rejects with this when a request is aborted.
    if (name === 'AbortError' && message === 'The user aborted a request.') {
      event.preventDefault();
      return;
    }
    if (!(reason instanceof Error)) return;
    const description = describe(reason, '');
    if (isIgnored(description)) return;
    report(
      'Unhandled rejection',
      description,
      reason.stack?.match(LOCATION_RE)?.[0],
      reason.stack
    );
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
};
